import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowLeft, FiLogOut, FiAlertCircle, FiBookOpen } from "react-icons/fi";
import Validador from "./valida/Validador";
import ValidadorRentas from "./valida/ValidadorRentas";
import MapaInmuebles from "./MapaInmuebles";
import { stopTour } from "@ipejal/tour-manager";

/**
 * TramiteDirecto — Componente que salta la pantalla de selección de trámites
 * y va directo al Validador para un tipo de préstamo específico.
 *
 * Se accede vía ruta: /tramite/:nombreTramite
 * Ejemplo: /tramite/Rentas
 *
 * El nombreTramite en la URL debe coincidir (case-insensitive) con el campo
 * `nombre` que devuelve la API /tipoPrestamos.
 */
export default function TramiteDirecto() {
  const { nombreTramite } = useParams();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const containerRef = useRef(null);

  const [user, setUser] = useState(null);
  const [prestamo, setPrestamo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mapaAbierto, setMapaAbierto] = useState(false);
  const [numeroAfiliado, setNumeroAfiliado] = useState(null);
  const [bloquearSeleccionInmuebles, setBloquearSeleccionInmuebles] = useState(false);
  const [folioSeleccionado, setFolioSeleccionado] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [preregistrosList, setPreregistrosList] = useState([]);
  const [rentasList, setRentasList] = useState([]);

  // Estado para el modal del reglamento de Rentas
  const [showRentasIntroModal, setShowRentasIntroModal] = useState(false);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (nombreTramite?.toLowerCase() === "rentas") {
      setShowRentasIntroModal(true);
    }
  }, [nombreTramite]);

  useEffect(() => {
    if (!showRentasIntroModal) return;
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showRentasIntroModal]);

  // 1. Obtener datos del usuario (igual que PreRegistroLayout)
  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

    if (!token) {
      setUser(null);
      return;
    }
    try {
      const decoded = jwtDecode(token);
      setUser({ ...decoded, ...storedUser });
      setNumeroAfiliado(storedUser?.username || decoded?.username || null);
    } catch {
      setUser(null);
    }
  }, []);

  // Procesar los datos recibidos de Validador para determinar bloqueo de selección de inmuebles
  const handleDataLoaded = useCallback(({ rentasTramiteList, datos }) => {
    if (!rentasTramiteList && !datos) return;
    const list = rentasTramiteList || [];
    const rawList = datos || [];
    setRentasList(list);
    setPreregistrosList(rawList);

    let ultimoPreregistroCancelado = false;
    if (rawList.length > 0) {
      const sorted = [...rawList].sort((a, b) => b.id_preregistro - a.id_preregistro);
      const latest = sorted[0];
      if (latest && latest.nombre_estado?.toUpperCase() === "CANCELADO") {
        ultimoPreregistroCancelado = true;
      }
    }

    const latestRentas = list[0];
    const estatusRentas = latestRentas?.tramite_estatus ? String(latestRentas.tramite_estatus).toUpperCase().trim() : "";
    const estatusFinalizados = ["CANCELADO", "VENCIDO", "CONCLUIDO"];
    const rentasInactivo = estatusFinalizados.includes(estatusRentas);

    // Solo debe bloquear si hay algún trámite ACTIVO en Rentas (distinto de CANCELADO, VENCIDO o CONCLUIDO)
    const shouldBlock = list.some((item) => {
      if (!item) return false;
      const estatus = item.tramite_estatus ? String(item.tramite_estatus).toUpperCase().trim() : "";
      
      if (estatusFinalizados.includes(estatus)) return false;

      const tieneEstatus = estatus !== "" && estatus !== "NULL" && estatus !== "NULO";
      return tieneEstatus;
    });

    if (rentasInactivo || ultimoPreregistroCancelado) {
      setBloquearSeleccionInmuebles(false);
    } else {
      setBloquearSeleccionInmuebles(shouldBlock);
    }
  }, []);

  // 2. Buscar el tipo de préstamo en la API y setear IDs en localStorage
  useEffect(() => {
    if (!apiUrl || !nombreTramite) return;

    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const endpoint =
      storedUser?.userType?.toUpperCase() === "DEUDO"
        ? "getTipoPrestamosSinLogin"
        : "tipoPrestamos";

    setCargando(true);
    setError(null);

    fetch(`${apiUrl}/${endpoint}`)
      .then((res) => res.json())
      .then((json) => {
        if (!json.data) {
          setError("No se pudo obtener el catálogo de trámites.");
          setCargando(false);
          return;
        }

        const activos = json.data.filter((t) => t.es_activo && Number(t.id_tipo_prestamo) !== 7);
        const found = activos.find(
          (t) =>
            t.nombre?.toLowerCase() === decodeURIComponent(nombreTramite).toLowerCase()
        );

        if (!found) {
          setError(
            `El trámite "${decodeURIComponent(nombreTramite)}" no fue encontrado o no está activo.`
          );
          setCargando(false);
          return;
        }

        // Guardar IDs en localStorage como lo hace RevealBento al seleccionar
        localStorage.setItem("id_tipo_prestamo", found.id_tipo_prestamo);
        localStorage.setItem("id_catalogo_tramite", found.id_catalogo_tramite);
        localStorage.removeItem("id_sede");

        setPrestamo(found);
        setCargando(false);
      })
      .catch((e) => {
        console.error("Error al buscar el trámite:", e);
        setError("Error de conexión al obtener los trámites.");
        setCargando(false);
      });
  }, [apiUrl, nombreTramite]);

  const isMutatingRef = useRef(false);

  // 3. Reemplazar botones de "Agendar Cita" por "Seleccionar Inmuebles" via DOM
  const reemplazarBotonesCita = useCallback(() => {
    if (!containerRef.current || isMutatingRef.current) return;
    isMutatingRef.current = true;
    try {
      const botonesCitaDesktop = Array.from(containerRef.current.querySelectorAll('[data-tour="agendar-cita-btn"]'));
      const botonesCitaMobile = Array.from(containerRef.current.querySelectorAll('[data-tour="agendar-cita-btn-movil"]'));

      const procesarBotones = (botones, isMobileBtn) => {
        if (bloquearSeleccionInmuebles) {
          botones.forEach((btnCita) => {
            btnCita.style.display = "none";
          });
          const botonesInmuebles = containerRef.current.querySelectorAll(isMobileBtn ? ".btn-inmuebles-rentas-movil" : ".btn-inmuebles-rentas-desktop");
          botonesInmuebles.forEach((btnInmueble) => {
            btnInmueble.remove();
          });
          return;
        }

        botones.forEach((btnCita) => {
          const parent = btnCita.parentElement;
          if (!parent) return;

          const folio = btnCita.getAttribute("data-folio");
          const cleanFolio = (f) => f ? String(f).toUpperCase().replace(/[^A-Z0-9]/g, '') : '';
          const cleanRowFolio = cleanFolio(folio);

          const prMatch = preregistrosList.find(p => cleanFolio(p.folio) === cleanRowFolio);
          const isDocCancelado = prMatch?.nombre_estado?.toUpperCase() === "CANCELADO";

          const rMatch = rentasList.find(item => {
            if (!item || !item.folio_tramite) return false;
            const baseFolio = item.folio_tramite.endsWith('R')
              ? item.folio_tramite.slice(0, -1)
              : item.folio_tramite;
            return cleanRowFolio === cleanFolio(baseFolio) || cleanRowFolio === cleanFolio(item.folio_tramite);
          });
          const estatusRentasStr = rMatch?.tramite_estatus?.toUpperCase()?.trim() || "";
          const isRentasCancelado = estatusRentasStr === "CANCELADO";

          // Si ya existe en la API de Rentas (tiene Folio fila virtual / Estatus Rentas) o el prerregistro está cancelado,
          // NO debe mostrar el botón de Seleccionar Inmuebles.
          const yaTieneRegistroRentas = Boolean(rMatch);

          if (isDocCancelado || isRentasCancelado || yaTieneRegistroRentas) {
            btnCita.style.display = "none";
            const existingBtn = parent.querySelector(isMobileBtn ? ".btn-inmuebles-rentas-movil" : ".btn-inmuebles-rentas-desktop");
            if (existingBtn) existingBtn.remove();
            const existingRenovarBtn = parent.querySelector(isMobileBtn ? ".btn-renovar-rentas-movil" : ".btn-renovar-rentas-desktop");
            if (existingRenovarBtn) existingRenovarBtn.remove();
            return;
          }

          const classTarget = isMobileBtn ? "btn-inmuebles-rentas-movil" : "btn-inmuebles-rentas-desktop";
          if (parent.querySelector(`.${classTarget}`)) {
            btnCita.style.display = "none";
            return;
          }

          btnCita.style.display = "none";

          const btnInmuebles = document.createElement("button");
          btnInmuebles.setAttribute("data-tour", isMobileBtn ? "seleccionar-inmuebles-btn-movil" : "seleccionar-inmuebles-btn");
          btnInmuebles.className = `${classTarget} flex items-center justify-center gap-2 px-4 h-10 bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl text-[11px] font-extrabold uppercase tracking-wider transition-all duration-200 whitespace-nowrap active:scale-95 shadow-md`;
          
          if (isMobileBtn) {
            btnInmuebles.style.cssText = "box-shadow: 0 4px 6px -1px rgba(5,150,105,0.2); width: 100%;";
          } else {
            btnInmuebles.style.cssText = "box-shadow: 0 4px 6px -1px rgba(5,150,105,0.2);";
          }

          btnInmuebles.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg><span>Seleccionar Inmuebles</span>';
          btnInmuebles.addEventListener("click", (e) => {
            e.stopPropagation();
            const folioVal = btnCita.getAttribute("data-folio");
            setFolioSeleccionado(folioVal);
            setMapaAbierto(true);
          });

          parent.insertBefore(btnInmuebles, btnCita.nextSibling);
        });
      };

      procesarBotones(botonesCitaDesktop, false);
      procesarBotones(botonesCitaMobile, true);
    } finally {
      isMutatingRef.current = false;
    }
  }, [bloquearSeleccionInmuebles, preregistrosList, rentasList]);

  useEffect(() => {
    if (!containerRef.current || cargando) return;

    // Ejecutar al inicio con un pequeño delay para que el DOM se renderice
    const timer = setTimeout(reemplazarBotonesCita, 300);

    // Observer para detectar cuando se agregan nuevos botones de cita
    const observer = new MutationObserver(() => {
      if (!isMutatingRef.current) {
        reemplazarBotonesCita();
      }
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [cargando, reemplazarBotonesCita]);

  const handleLogout = () => {
    localStorage.clear();
    window.location.replace("/");
  };

  const handleBack = () => {
    stopTour();
    if (nombreTramite?.toLowerCase() === "rentas") {
      window.location.href = "/tramite/Rentas";
    } else {
      navigate("/preregistro", { replace: true });
    }
  };

  // --- Estado: Cargando ---
  if (cargando) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50/50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4 mx-auto" />
          <p className="text-sm text-gray-500 font-medium">
            Buscando trámite...
          </p>
        </div>
      </div>
    );
  }

  // --- Estado: Error ---
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50/50">
        <div className="text-center max-w-md px-6">
          <div className="size-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <FiAlertCircle className="text-3xl text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Trámite no disponible
          </h2>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-bold text-sm hover:bg-primary/90 transition-all active:scale-95"
          >
            <FiArrowLeft />
            Ir al listado de trámites
          </button>
        </div>
      </div>
    );
  }

  // --- Estado: Trámite encontrado → Render directo del Validador ---
  return (
    <div className="min-h-screen bg-slate-50/80 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100 flex items-center h-20 px-6 shadow-sm">
        <div className="w-full flex items-center justify-between">
          <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={handleBack}
            className="flex items-center gap-2 text-primary font-bold text-xs hover:bg-primary/5 px-4 py-2 rounded-xl transition-all active:scale-95 group"
          >
            <FiArrowLeft className="text-base group-hover:-translate-x-1 transition-transform" />
            <span className="uppercase tracking-widest mt-0.5">Volver</span>
          </motion.button>

          <div className="flex items-center gap-2 md:gap-4">
            <div className="flex flex-col items-end mr-1 md:mr-0 text-right">
              <span className="hidden md:block text-[9px] font-bold text-primary uppercase tracking-[0.15em]">
                IPEJAL Digital
              </span>
              <div className="flex flex-col items-end">
                <span className="text-[11px] md:text-xs font-bold text-gray-800 tracking-tight">
                  {user?.displayName ||
                    `${user?.firstName || ""} ${user?.firstLastName || ""} ${user?.secondLastName || ""}`.trim() ||
                    "Usuario"}
                </span>
                <span className="text-[9px] md:text-[10px] text-gray-500 font-semibold tracking-tight">
                  {[
                    user?.username ? `Usuario: ${user.username}` : null,
                    user?.rfc ? `RFC: ${user.rfc}` : null,
                    user?.email ? user.email : null,
                  ]
                    .filter(Boolean)
                    .join(" | ")}
                </span>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="size-10 md:size-11 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-xl transition-colors bg-gray-50 md:bg-transparent shadow-sm md:shadow-none"
              title="Cerrar Sesión"
            >
              <FiLogOut className="text-lg md:text-xl" />
            </button>
          </div>
        </div>
      </header>

      {/* Contenido principal — Validador directo */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
        <div
          ref={containerRef}
          className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden"
        >
          {nombreTramite?.toLowerCase() === "rentas" ? (
            <ValidadorRentas
              tipoPrestamo={prestamo.nombre}
              nombreLargo={prestamo.descripcion}
              skipValidarDisponibilidad={true}
              apiUrlRentas={process.env.REACT_APP_API_URL_RENTAS}
              refreshTrigger={refreshCounter}
              onDataLoaded={handleDataLoaded}
              isIntroModalOpen={showRentasIntroModal}
            />
          ) : (
            <Validador
              tipoPrestamo={prestamo.nombre}
              nombreLargo={prestamo.descripcion}
              skipValidarDisponibilidad={true}
              apiUrlRentas={process.env.REACT_APP_API_URL_RENTAS}
              refreshTrigger={refreshCounter}
              onDataLoaded={handleDataLoaded}
              isIntroModalOpen={showRentasIntroModal}
            />
          )}
        </div>
      </main>

      {/* Modal del mapa de inmuebles */}
      <MapaInmuebles
        isOpen={mapaAbierto && !bloquearSeleccionInmuebles}
        onClose={() => setMapaAbierto(false)}
        onSelect={(seleccionados) => {
          console.log("[Rentas] Registro exitoso. Conjuntos seleccionados:", seleccionados);
          // Cerrar el mapa
          setMapaAbierto(false);
          // Disparar refresco del Validador (tabla + estado Rentas)
          setRefreshCounter((c) => c + 1);
        }}
        codigoAfiliado={numeroAfiliado}
        correoElectronico={user?.email}
        apiUrl={process.env.REACT_APP_API_URL_RENTAS}
        folio={folioSeleccionado}
      />

      {/* Modal del Reglamento de Rentas (Premium, Light & Modern) */}
      <AnimatePresence>
        {showRentasIntroModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop con desenfoque de fondo */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/45 backdrop-blur-md"
            />

            {/* Contenedor del Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 20 }}
              transition={{ type: "spring", damping: 30, stiffness: 200 }}
              className="relative bg-white border border-slate-200/80 w-full max-w-5xl h-[85vh] md:h-[75vh] rounded-[2.5rem] shadow-[0_25px_60px_-15px_rgba(15,23,42,0.18)] overflow-hidden flex flex-col z-10 p-6 md:p-10 text-slate-800"
            >
              {/* Barra de progreso de lectura al tope del modal */}
              <div className="absolute top-0 left-0 right-0 h-[4px] bg-slate-100">
                <motion.div
                  initial={{ width: "100%" }}
                  animate={{ width: "0%" }}
                  transition={{ duration: 3, ease: "linear" }}
                  className="h-full bg-primary"
                />
              </div>

              {/* Encabezado */}
              <div className="flex items-start gap-4 mb-6 mt-2">
                <div className="p-4 bg-primary/5 text-primary rounded-2xl border border-primary/10 shadow-inner flex-shrink-0">
                  <FiBookOpen className="size-7" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 tracking-tight uppercase">
                    Reglamento de Arrendamiento
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 font-bold tracking-wider uppercase mt-1">
                    Bases y condiciones de asignación de viviendas IPEJAL
                  </p>
                </div>
              </div>

              {/* Contenido / Texto del Reglamento (Maximizado, Súper Leíble y Formateado) */}
              <div className="flex-1 overflow-y-auto bg-slate-50 border border-slate-100 rounded-[2rem] p-6 md:p-8 custom-scrollbar">
                <div className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-600 font-medium leading-relaxed md:leading-loose text-justify space-y-4">
                  <p>
                    El <strong>arrendamiento habitacional</strong> está regulado por el <em>Reglamento General de Prestaciones, Derechos y Obligaciones de Afiliados y Pensionados de la Dirección de Pensiones del Estado</em>, el cual establece las bases, modalidades, criterios y procedimientos que se deberán seguir para su otorgamiento.
                  </p>
                  <p>
                    Tratándose de inmuebles destinados a vivienda, <strong>únicamente podrán ser arrendatarios</strong> los afiliados y los pensionados que se encuentren en <strong>servicio activo</strong>, laborando para entidades y dependencias adscritas al régimen de la Ley.
                  </p>
                  <p>
                    El <strong>IPEJAL</strong>, escuchando las sugerencias de los Organismos Sindicales integrantes del Consejo Directivo, mantendrá los mecanismos adecuados para asignar las viviendas en arrendamiento entre sus afiliados y pensionados solicitantes, atendiendo a la demanda y a la disponibilidad de inmuebles destinados a ese fin.
                  </p>
                  <p>
                    Para tal efecto, por conducto de su área de rentas, elaborará <strong>listas de solicitantes</strong> que permitan distribuir de forma equitativa las viviendas que sean susceptibles de arrendarse, <strong>otorgando preferencia a aquellos que más antigüedad tengan en espera</strong>.
                  </p>
                  <p>
                    Tales listados se llevarán por unidad habitacional o en forma general, conforme a las preferencias de inmuebles de los solicitantes, los cuales serán sistematizados <strong>garantizando la transparencia y asignación de conformidad a su orden de prelación</strong>.
                  </p>
                </div>
              </div>

              {/* Botón de Acción y Temporizador */}
              <div className="flex items-center justify-between border-t border-slate-100 pt-6 mt-6">
                <span className="text-[10px] sm:text-xs text-slate-400 font-black uppercase tracking-wider">
                  {countdown > 0 ? `Por favor lea el reglamento (${countdown}s)` : "Reglamento leído"}
                </span>
                <button
                  disabled={countdown > 0}
                  onClick={() => setShowRentasIntroModal(false)}
                  className="px-8 sm:px-10 py-4 bg-primary hover:bg-[#6b40b8] disabled:bg-slate-100 text-white disabled:text-slate-400 rounded-2xl font-bold text-xs sm:text-sm uppercase tracking-widest shadow-lg shadow-primary/10 disabled:shadow-none transition-all active:scale-95 flex items-center justify-center"
                >
                  <span>Aceptar y Proceder</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
